import { searchJyotishaTopics, JYOTISHA_TOPIC_SEARCH_INDEX } from '../data/jyotishaTopicSearchIndex';
import {
  NUMEROLOGY_SYLLABUS_TOPIC_IDS,
  searchNumerologyTopics,
} from 'schools/numerology/data/numerologySearchIndex';
import { SYLLABUS_TAB_IDS } from '../data/jyotishaLessonNumbers';
import {
  detectJyotishaKindFromQuery,
  getJyotishaTopicKindLabel,
} from '../utils/jyotishaTopicKinds';
import { normalizeAskGuruQuery } from './askBhriguQueryNormalize';

function fold(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** @param {string} topicId @param {'jyotisha' | 'numerology'} section */
function syllabusOrderIndex(topicId, section) {
  const list = section === 'numerology' ? NUMEROLOGY_SYLLABUS_TOPIC_IDS : SYLLABUS_TAB_IDS;
  const idx = list.indexOf(topicId);
  return idx >= 0 ? idx : Number.MAX_SAFE_INTEGER;
}

/** @param {{ topicId: string; rank: number; section?: string }} a @param {{ topicId: string; rank: number; section?: string }} b */
function compareSearchResults(a, b) {
  const orderDiff =
    syllabusOrderIndex(a.topicId, a.section ?? 'jyotisha') - syllabusOrderIndex(b.topicId, b.section ?? 'jyotisha');
  if (orderDiff !== 0) return orderDiff;
  return b.rank - a.rank;
}

const READING_PATH_HINTS = {
  'reading-chart': {
    en: 'This sounds like chart reading - explore the Reading Charts chapter, then check houses and dashas.',
    hi: 'यह चार्ट पठन जैसा लगता है - Reading Charts अध्याय देखें, फिर भाव और दशा पढ़ें।',
  },
  houses: {
    en: 'This sounds like house lord placement - start with lesson 2.8 House lords in all houses, then try the house lord calculator.',
    hi: 'यह भाव स्वामी स्थिति जैसा लगता है - पहले 2.8 भावेश सभी भावों में पढ़ें, फिर house lord calculator देखें।',
  },
  lesson: {
    en: 'Start with the matched lesson below, then follow related topics in the same chapter.',
    hi: 'नीचे मिले पाठ से शुरू करें, फिर उसी अध्याय के संबंधित विषय देखें।',
  },
  panchang: {
    en: 'This relates to Panchang / muhurta - check Panchang lessons and daily timing basics.',
    hi: 'यह पंचांग / मुहूर्त से जुड़ा है - पंचांग पाठ और समय-मुहूर्त की मूल बातें देखें।',
  },
  chart: {
    en: 'This looks like chart calculation - open the chart calculator lessons first.',
    hi: 'यह कुंडली गणना जैसा है - पहले चार्ट कैलकुलेटर पाठ खोलें।',
  },
};

/** @param {string} topicId */
function topicIndexEntry(topicId) {
  return JYOTISHA_TOPIC_SEARCH_INDEX.find((e) => e.id === topicId) ?? null;
}

/**
 * @param {import('../data/jyotishaTopicSearchIndex').typeof JYOTISHA_TOPIC_SEARCH_INDEX[0]} entry
 * @param {number} rank
 */
function toLessonResult(entry, rank) {
  return {
    topicId: entry.id,
    label: entry.tabLabel,
    groupLabel: entry.groupLabel,
    snippet: entry.matchSnippet || '',
    rank,
    section: 'jyotisha',
  };
}

/**
 * @param {string} query
 * @param {{ limit?: number; includeNumerology?: boolean }} [opts]
 */
export function searchAskGuruContent(query, opts = {}) {
  const limit = opts.limit ?? 8;
  const parsed = normalizeAskGuruQuery(query);
  const searchQuery = parsed.normalizedQuery || query;

  const jyotishaRaw = searchJyotishaTopics(searchQuery);
  /** @type {Map<string, ReturnType<typeof toLessonResult>>} */
  const byId = new Map();

  parsed.boostTopicIds.forEach((topicId, i) => {
    const entry = topicIndexEntry(topicId);
    if (entry) {
      byId.set(topicId, toLessonResult({ ...entry, matchSnippet: '' }, limit + 100 - i));
    }
  });

  jyotishaRaw.forEach((entry, i) => {
    const existing = byId.get(entry.id);
    const rank = existing?.rank ?? limit - i;
    byId.set(entry.id, toLessonResult(entry, rank));
  });

  const jyotisha = [...byId.values()].sort(compareSearchResults).slice(0, limit);

  const includeNumerology =
    opts.includeNumerology !== false && !parsed.isJyotishaSignal && parsed.intent !== 'house_lord_placement';

  const numerology = includeNumerology
    ? searchNumerologyTopics(searchQuery)
        .slice(0, Math.max(2, limit - jyotisha.length))
        .map((entry, i) => ({
          topicId: entry.id,
          label: entry.tabLabel,
          groupLabel: entry.groupLabel,
          snippet: entry.matchSnippet || '',
          rank: Math.max(2, limit - jyotisha.length) - i,
          section: 'numerology',
        }))
    : [];

  return [...jyotisha, ...numerology].sort(compareSearchResults).slice(0, limit);
}

/**
 * @param {string} query
 * @param {boolean} showEnglish
 */
export function getProbableReadingPath(query, showEnglish) {
  const parsed = normalizeAskGuruQuery(query);
  const folded = fold(parsed.normalizedQuery || query);

  if (parsed.intent === 'house_lord_placement') {
    const hint = READING_PATH_HINTS.houses;
    return {
      kind: 'houses',
      kindLabel: showEnglish ? 'Houses' : 'गृह',
      hintEn: hint.en,
      hintHi: hint.hi,
      hint: showEnglish ? hint.en : hint.hi,
    };
  }

  const hasHouseLord =
    parsed.contentTokens.some((t) => ['lord', 'lords', 'bhavesh', 'lagnesh'].includes(t)) &&
    parsed.contentTokens.some((t) => ['house', 'houses', 'bhava', '5th', '8th'].includes(t) || /^\d{1,2}(st|nd|rd|th)$/.test(t));

  if (hasHouseLord) {
    const hint = READING_PATH_HINTS.houses;
    return {
      kind: 'houses',
      kindLabel: showEnglish ? 'Houses' : 'गृह',
      hintEn: hint.en,
      hintHi: hint.hi,
      hint: showEnglish ? hint.en : hint.hi,
    };
  }

  const kind = detectJyotishaKindFromQuery(folded);
  const kindLabel = kind ? getJyotishaTopicKindLabel(kind, showEnglish) : null;
  const hint = kind ? READING_PATH_HINTS[kind] || READING_PATH_HINTS.lesson : READING_PATH_HINTS.lesson;

  return {
    kind,
    kindLabel,
    hintEn: hint.en,
    hintHi: hint.hi,
    hint: showEnglish ? hint.en : hint.hi,
  };
}
